import { functionsUrl, supabase } from './client';

/**
 * The two auth flows, as the UI sees them.
 *
 * Sign-in is deliberately two calls: `signIn` checks the password and returns a
 * ticket, `verifyOtp` exchanges the emailed code for a session. The password is
 * never sent a second time — that is what the ticket is for.
 */

export interface SignInResult {
  otpTicket: string;
  expiresIn: number;
  /** Where the code was sent. Resolved server-side, since the user may have
   *  typed a username and need not know which address is on file. */
  email: string;
}

export type AuthFailure =
  | 'invalid_credentials'
  | 'rate_limited'
  | 'invalid_code'
  | 'expired'
  | 'weak_password'
  | 'network'
  | 'unknown';

export class AuthError extends Error {
  constructor(
    public readonly kind: AuthFailure,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * The API answers unhappy paths vaguely on purpose — it will not say whether an
 * account exists or whether the password was wrong. The UI has to be equally
 * careful, so these messages never distinguish those cases either.
 */
function toAuthError(status: number, body: unknown): AuthError {
  if (status === 429) {
    return new AuthError(
      'rate_limited',
      'Too many attempts. Wait a few minutes before trying again.',
    );
  }
  if (status === 422) {
    const detail =
      typeof body === 'object' && body !== null && 'message' in body
        ? (body as Record<'message', unknown>).message
        : undefined;
    return new AuthError(
      'weak_password',
      typeof detail === 'string' ? detail : 'That password does not meet the requirements.',
    );
  }
  if (status === 401) {
    return new AuthError('invalid_credentials', 'Those details were not accepted.');
  }
  return new AuthError('unknown', 'Something went wrong. Please try again.');
}

async function post(path: string, body: unknown): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(functionsUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthError('network', 'Could not reach the server. Check your connection.');
  }

  const parsed: unknown = await res.json().catch(() => null);
  if (!res.ok) throw toAuthError(res.status, parsed);
  return parsed;
}

/**
 * Step 1 of sign-in: verify the password, trigger the emailed code.
 *
 * `identifier` is a username or an email address — staff know their username,
 * GoTrue only knows emails, and the function resolves between them. The result
 * carries the address the code was actually sent to, which is the only way the
 * next screen can name the mailbox when a username was used.
 */
export async function signIn(identifier: string, password: string): Promise<SignInResult> {
  return (await post('auth-otp/issue', { identifier, password })) as SignInResult;
}

/**
 * Step 2: exchange the code for a session.
 *
 * The function returns a one-time token hash rather than a session directly;
 * supabase-js redeems it, which is what puts the session into storage and
 * starts automatic refresh.
 */
export async function verifyOtp(otpTicket: string, code: string): Promise<void> {
  const data = (await post('auth-otp/verify', { otpTicket, code })) as {
    email: string;
    tokenHash: string;
  };

  const { error } = await supabase().auth.verifyOtp({
    type: 'magiclink',
    token_hash: data.tokenHash,
  });
  if (error) throw new AuthError('unknown', 'Could not complete sign-in. Please try again.');
}

/**
 * Ask for a reset code.
 *
 * Always succeeds from the caller's point of view — the server will not reveal
 * whether the account exists, and neither should the UI.
 */
export async function requestReset(identifier: string): Promise<void> {
  await post('auth-reset/request', { identifier });
}

export async function verifyResetCode(email: string, code: string): Promise<string> {
  const data = (await post('auth-reset/verify', { email, code })) as { resetTicket: string };
  return data.resetTicket;
}

/**
 * Set the new password.
 *
 * Deliberately does NOT sign the user in — they then sign in with the new
 * password and a fresh code. See decision #12.
 */
export async function completeReset(resetTicket: string, password: string): Promise<void> {
  await post('auth-reset/complete', { resetTicket, password });
}

export async function signOut(): Promise<void> {
  await supabase().auth.signOut();
}
